import React from 'react'
import { Location } from 'history'

// Constants
import { RouteProps, routes } from '@sketch/constants'

// Hooks
import { getIsForInAppPresentation } from 'modules/user/hooks/useSketchSource/useSketchSource'

// Components
import { useToast } from '@sketch/toasts'
import { LinkButton, Link } from '@sketch/components'
import ForgotPasswordForm from 'components/ForgotPasswordForm'
import IndexLayout, { Title, Subtitle } from 'components/IndexLayout'

// Styles
import { Wrapper, Text, Line } from './ForgotPasswordView.styles'

// Types
interface LocationState {
  from: string
}

interface ForgotPasswordViewProps extends RouteProps<'FORGOT_PASSWORD'> {
  location: Location<LocationState>
}

const ForgotPasswordView: React.FC<ForgotPasswordViewProps> = props => {
  const {
    location: { state },
  } = props
  const { showToast } = useToast()
  const isForInAppPresentation = getIsForInAppPresentation()

  return (
    <IndexLayout
      headerLink="none-with-border"
      variant="orange"
      blockLogoLink={isForInAppPresentation}
    >
      <ForgotPasswordForm
        onCompleted={data => {
          const errors = data.userRequestPasswordReset?.errors || []

          if (errors.length > 0) {
            const apiErrors = errors.map(error => error?.message)
            showToast(apiErrors.join('; '), 'negative')
          }
        }}
        onError={error => {
          showToast(error.message, 'negative')
        }}
      >
        {formikProps =>
          formikProps.successful ? (
            <Wrapper>
              <Title>Forgot Your Password?</Title>
              <Subtitle>
                If there is an account associated with this email address you
                will receive a password reset email.
              </Subtitle>
              <LinkButton
                to={{ pathname: routes.SIGN_IN.create({}), state }}
                variant="primary"
              >
                Go back to Sign in
              </LinkButton>
            </Wrapper>
          ) : (
            <>
              <Title>Forgot Your Password?</Title>
              <Subtitle>
                Enter the email address you use to sign in to Sketch.
              </Subtitle>
              <ForgotPasswordForm.Fields {...formikProps} />
              <ForgotPasswordForm.Submit {...formikProps} />
              {!isForInAppPresentation && (
                <>
                  <Line />
                  <Text>
                    Already have a Sketch account?{' '}
                    <Link
                      variant="primary"
                      to={routes.SIGN_IN.create({})}
                      isUnderlined
                    >
                      Sign in
                    </Link>
                  </Text>
                  <Text>
                    New to Sketch?{' '}
                    <Link
                      variant="primary"
                      isUnderlined
                      to={routes.SIGN_UP.create({})}
                    >
                      Create an account
                    </Link>
                  </Text>
                </>
              )}
            </>
          )
        }
      </ForgotPasswordForm>
    </IndexLayout>
  )
}

export default ForgotPasswordView
